/**
 * MediaManager — Admin panel section to upload/replace image slots and
 * manage per-division galleries (add / delete / reorder via drag).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, errMsg } from "@/lib/config";
import { useMedia } from "@/lib/media";
import { toast } from "sonner";
import { UploadCloud, Trash2, Loader2, ImagePlus, GripVertical, RefreshCw } from "lucide-react";

const GALLERY_DIVISIONS = [
  { id: "irrigazione", label: "Irrigazione" },
  { id: "trattamento-acqua", label: "Trattamento Acque" },
  { id: "automazioni", label: "Automazioni" },
  { id: "elettrico", label: "Elettrico" },
  { id: "fotovoltaico", label: "Fotovoltaico" },
  { id: "antiincendio", label: "Antincendio" },
];

const fullUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/")) return `${API_BASE}${u}`;
  return u;
};

/** Single slot editor with drag-and-drop upload zone. */
function SlotCard({ slot, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.put(`/admin/media/slots/${slot.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${slot.label}" aggiornato`);
      onChanged();
    } catch (err) {
      toast.error(errMsg(err, "Upload fallito"));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Ripristinare l'immagine di default per "${slot.label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/media/slots/${slot.key}`);
      toast.success("Ripristinato al default");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err, "Errore"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a]" data-testid={`slot-card-${slot.key}`}>
      <div className="p-4 border-b border-[#1a1a1a]">
        <div className="font-mono-tech text-[10px] text-white/40 uppercase">{slot.key}</div>
        <div className="text-white text-sm mt-1">{slot.label}</div>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setHover(false);
          const f = e.dataTransfer.files?.[0];
          if (f) upload(f);
        }}
        className={`relative aspect-[16/10] cursor-pointer transition-colors ${hover ? "bg-[#ff4b00]/10 border-2 border-dashed border-[#ff4b00]" : "bg-black"}`}
        onClick={() => inputRef.current?.click()}
      >
        {slot.url ? (
          <img src={fullUrl(slot.url)} alt={slot.label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
            <ImagePlus size={28} />
            <div className="font-mono-tech text-[10px] mt-2">DEFAULT (nessun override)</div>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Loader2 className="animate-spin text-[#ff4b00]" />
          </div>
        )}
        {hover && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="font-mono-tech text-xs text-[#ff4b00]">RILASCIA PER CARICARE</div>
          </div>
        )}
      </div>
      <div className="flex border-t border-[#1a1a1a]">
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`slot-upload-${slot.key}`}
          className="flex-1 py-3 text-white/70 hover:text-[#ff4b00] font-mono-tech text-[11px] flex items-center justify-center gap-2 transition-colors"
        >
          <UploadCloud size={12} /> {slot.url ? "Sostituisci" : "Carica"}
        </button>
        {slot.url && (
          <button
            onClick={clear}
            disabled={busy}
            data-testid={`slot-clear-${slot.key}`}
            className="px-4 py-3 text-white/50 hover:text-red-400 border-l border-[#1a1a1a] transition-colors"
            title="Ripristina default"
          >
            <RefreshCw size={12} />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
}

/** Gallery editor for a single division: drag-reorder + add + delete. */
function GalleryEditor({ divisionId, label }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const inputRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/media/gallery/${divisionId}`);
      setItems(r.data || []);
    } catch (err) {
      toast.error(errMsg(err, "Errore caricamento galleria"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [divisionId]);

  const addImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/media/gallery/${divisionId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Immagine aggiunta");
      await refresh();
    } catch (err) {
      toast.error(errMsg(err, "Upload fallito"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (itemId) => {
    if (!window.confirm("Rimuovere questa immagine?")) return;
    try {
      await api.delete(`/admin/media/gallery/${itemId}`);
      toast.success("Immagine rimossa");
      setItems((prev) => prev.filter((it) => it.id !== itemId));
    } catch (err) {
      toast.error(errMsg(err, "Errore"));
    }
  };

  const persistOrder = async (nextItems) => {
    try {
      await api.put(`/admin/media/gallery/${divisionId}/reorder`, {
        ids: nextItems.map((it) => it.id),
      });
    } catch (err) {
      toast.error(errMsg(err, "Riordino fallito"));
      refresh();
    }
  };

  const onDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setItems(next);
    setDragIdx(null);
    persistOrder(next);
  };

  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a]" data-testid={`gallery-${divisionId}`}>
      <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between">
        <div>
          <div className="font-mono-tech text-[10px] text-white/40 uppercase">Galleria · {divisionId}</div>
          <div className="text-white text-sm mt-1">{label}</div>
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`gallery-add-${divisionId}`}
          className="btn-ghost text-xs flex items-center gap-2"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
          Aggiungi immagine
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => addImage(e.target.files?.[0])}
        />
      </div>
      <div className="p-4">
        {loading ? (
          <div className="text-white/40 font-mono-tech text-xs">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 font-mono-tech text-xs">
            Nessuna immagine caricata — verranno mostrate le foto di default del sito.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {items.map((it, idx) => (
              <div
                key={it.id}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(idx)}
                className={`relative aspect-square bg-black border border-[#1a1a1a] group ${dragIdx === idx ? "opacity-40" : ""}`}
                data-testid={`gallery-item-${it.id}`}
              >
                <img src={fullUrl(it.url)} alt="" className="w-full h-full object-cover" />
                <div className="absolute top-1 left-1 bg-black/70 text-[9px] font-mono-tech text-white px-1.5 py-0.5">
                  #{idx + 1}
                </div>
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <span className="bg-black/70 p-1 cursor-grab" title="Trascina per riordinare"><GripVertical size={12} className="text-white/80" /></span>
                  <button
                    onClick={() => remove(it.id)}
                    className="bg-black/70 hover:bg-red-500 p-1"
                    data-testid={`gallery-delete-${it.id}`}
                    title="Rimuovi"
                  >
                    <Trash2 size={12} className="text-white" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MediaManager() {
  const { refreshSlots } = useMedia();
  const [slots, setSlots] = useState([]);
  const [selectedDiv, setSelectedDiv] = useState(GALLERY_DIVISIONS[0].id);
  const [tab, setTab] = useState("slots");
  const [loading, setLoading] = useState(true);

  const loadSlots = async () => {
    try {
      const r = await api.get("/admin/media/slots");
      setSlots(r.data || []);
    } catch (err) {
      toast.error(errMsg(err, "Errore caricamento slot"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSlots(); }, []);

  const handleChanged = async () => {
    await loadSlots();
    await refreshSlots(); // update the live cache read by public pages
  };

  const currentDivLabel = useMemo(
    () => GALLERY_DIVISIONS.find((d) => d.id === selectedDiv)?.label,
    [selectedDiv],
  );

  return (
    <div data-testid="media-manager">
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setTab("slots")}
          data-testid="media-tab-slots"
          className={`px-4 py-2 font-mono-tech text-[11px] uppercase ${tab === "slots" ? "text-[#ff4b00] border-b border-[#ff4b00]" : "text-white/50"}`}
        >
          Slot fissi (9)
        </button>
        <button
          onClick={() => setTab("gallery")}
          data-testid="media-tab-gallery"
          className={`px-4 py-2 font-mono-tech text-[11px] uppercase ${tab === "gallery" ? "text-[#ff4b00] border-b border-[#ff4b00]" : "text-white/50"}`}
        >
          Gallerie divisioni
        </button>
      </div>

      {tab === "slots" && (
        <>
          <div className="font-mono-tech text-[10px] text-white/40 mb-4">
            Trascina un file oppure clicca su ogni riquadro per sostituire l'immagine. Formati: .jpg .png .webp · max 8MB.
          </div>
          {loading ? (
            <div className="text-white/40 font-mono-tech text-xs">Caricamento slot…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {slots.map((s) => (
                <SlotCard key={s.key} slot={s} onChanged={handleChanged} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === "gallery" && (
        <>
          <div className="flex flex-wrap gap-2 mb-6">
            {GALLERY_DIVISIONS.map((d) => (
              <button
                key={d.id}
                onClick={() => setSelectedDiv(d.id)}
                data-testid={`gallery-select-${d.id}`}
                className={`px-3 py-1.5 border font-mono-tech text-[11px] transition-colors ${selectedDiv === d.id ? "border-[#ff4b00] text-[#ff4b00]" : "border-[#1a1a1a] text-white/60 hover:text-white"}`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <GalleryEditor divisionId={selectedDiv} label={currentDivLabel} />
        </>
      )}
    </div>
  );
}
